'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { BrandLoader } from './BrandLoader'
import { useMessages } from '@/lib/i18n/client'
import messages from './NavigationOverlay.i18n'

// Overlay de pantalla completa para que un cambio de pantalla sea imposible
// de no notar (a diferencia de una barra fina arriba, que mucha gente no
// percibe). Se muestra al detectar el click en un <Link> interno y se oculta
// cuando pathname/searchParams cambian, es decir cuando la nueva ruta ya
// está montada. MIN_VISIBLE_MS evita un parpadeo de 1 frame en navegaciones
// instantáneas (con prefetch/caché) sin demorar artificialmente las lentas.
const MIN_VISIBLE_MS = 300

export function NavigationOverlay() {
  const t = useMessages(messages)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [visible, setVisible] = useState(false)
  const visibleRef = useRef(false)
  const shownAtRef = useRef(0)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  function show() {
    clearTimeout(hideTimeoutRef.current)
    shownAtRef.current = Date.now()
    visibleRef.current = true
    setVisible(true)
  }

  function hide() {
    visibleRef.current = false
    setVisible(false)
  }

  useEffect(() => {
    function findInternalLink(target: EventTarget | null): HTMLAnchorElement | null {
      if (!(target instanceof Element)) return null
      const anchor = target.closest('a')
      if (!anchor) return null
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || anchor.target === '_blank') return null
      if (anchor.hasAttribute('download')) return null
      let url: URL
      try {
        url = new URL(href, window.location.href)
      } catch {
        return null
      }
      if (url.origin !== window.location.origin) return null
      if (url.pathname === window.location.pathname && url.search === window.location.search) return null
      return anchor
    }

    function handleClick(e: MouseEvent) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      if (!findInternalLink(e.target)) return
      show()
    }

    // Bubble phase (no capture) — a propósito. Guards como
    // UnsavedChangesGuard interceptan en fase de captura y llaman
    // stopPropagation() para cancelar la navegación (ej. cambios sin
    // guardar); si este listener corriera en captura, no hay garantía de
    // orden entre ambos (dependería de cuál se registró primero) y podíamos
    // mostrar el loader para una navegación que el guard termina cancelando
    // — quedando visible para siempre, tapando el modal de confirmación. En
    // bubble, por spec del DOM, siempre corre después de que la fase de
    // captura completa, así que un stopPropagation() del guard evita que
    // este listener se entere del click.
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  useEffect(() => {
    if (!visibleRef.current) return
    const elapsed = Date.now() - shownAtRef.current
    const remaining = MIN_VISIBLE_MS - elapsed
    if (remaining <= 0) {
      hide()
    } else {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = setTimeout(hide, remaining)
    }
  }, [pathname, searchParams])

  if (!visible) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-background/60 backdrop-blur-sm"
    >
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-card px-8 py-6 shadow-lift">
        <BrandLoader size={56} />
        <p className="text-sm font-medium text-muted-foreground">{t.loading}</p>
      </div>
    </div>
  )
}
